import type { ReactElement } from "react";
import type {
  EnvironmentalResponse,
  SeismicHazardBand,
  UsfsCanopyBand,
  WildfireHazardBand
} from "@shared/api";
import type { ExternalContextReportData } from "../createPropertyReport";
import type { InspectorPresentation } from "./BaseLayersCard";
import { SidetextRow } from "./SidetextRow";
import { SubsectionHeader } from "./SubsectionHeader";

/**
 * EnvironmentalRiskCard — replaces the prior FloodRiskCard.
 *
 * Rolls together every "environmental risk to this parcel" signal we
 * surface today:
 *
 *   FROM external client-side fetch (FEMA NFHL + USFWS NWI):
 *     - FEMA flood zone designation
 *     - Flood polygon coverage of the parcel
 *     - NWI wetlands coverage of the parcel
 *
 *   FROM /v1/environmental (Phase 3 worker route):
 *     - USFS Wildfire Hazard Potential band
 *     - EPA Facility Registry Service proximity (count + Superfund flag)
 *     - USDA SSURGO soil drainage class
 *     - USDA SSURGO hydric soil rating
 *     - EPA AirNow current AQI (always shown when configured, even
 *       in the "Good" range — buyer asked for it explicitly)
 *
 * The card stays opt-in: when env data hasn't loaded yet (or the
 * Worker env doesn't have the source's API key), rows render `—`
 * rather than disappearing. That lets the layout stay stable
 * across the load.
 *
 * HUD Fair Market Rents is deliberately NOT rendered here — the data
 * is fetched but doesn't belong on a risk card and our custom-home /
 * builder buyer doesn't need it. The data is still in
 * `EnvironmentalResponse.fmr` for future investor surfaces.
 *
 * The card stays mounted under the same DOM id (`#flood-risk-mount`)
 * and inspectorAnchor (`floodRisk`) for surgical-revert safety —
 * those are stable string identifiers wired in 4+ places and renaming
 * them would expand the change surface. The visible label moves to
 * "Environmental Risk" but the internal id is unchanged.
 */

function pctRound(v: number): string {
  return `${v.toFixed(0)}%`;
}

function coverageValue(fraction: number | null | undefined): string {
  if (fraction == null) return "—";
  if (fraction > 0) return pctRound(fraction * 100);
  return "0% (None)";
}

function femaZoneValue(data: ExternalContextReportData | null): string {
  if (!data?.floodZone) return "—";
  return `${data.floodZone.code} · ${data.floodZone.description}`;
}

/**
 * Format the USFS WHP band into a user-facing string. The raw band
 * names are kebab-case for clean enum semantics; the display strings
 * here are what shows up in the card. `unknown` falls back to `—` so
 * the row looks the same as any other no-data row.
 */
function wildfireValue(env: EnvironmentalResponse | null): string {
  const hazard = env?.wildfire?.hazard;
  if (!hazard || hazard === "unknown") return "—";
  return wildfireDisplayName(hazard);
}

function wildfireDisplayName(band: WildfireHazardBand): string {
  switch (band) {
    case "very-low": return "Very low";
    case "low": return "Low";
    case "moderate": return "Moderate";
    case "high": return "High";
    case "very-high": return "Very high";
    case "non-burnable": return "Non-burnable";
    case "water": return "Water";
    case "unknown": return "—";
  }
}

/**
 * Combine the EPA proximity count + Superfund flag into one row. The
 * Superfund signal is the meaningful one — a nearby gas station or
 * dry cleaner registered with EPA is much less actionable than a
 * Superfund / NPL listing — so we surface it as a parenthetical when
 * present.
 */
function epaValue(env: EnvironmentalResponse | null): string {
  const epa = env?.epa;
  if (!epa) return "—";
  const count = epa.facilities.length;
  if (count === 0) return "None within 500 m";
  const noun = count === 1 ? "facility" : "facilities";
  const radius = `${epa.searchRadiusMeters} m`;
  return epa.superfundNearby
    ? `${count} ${noun} within ${radius} (Superfund nearby)`
    : `${count} ${noun} within ${radius}`;
}

function soilDrainageValue(env: EnvironmentalResponse | null): string {
  return env?.soil?.drainageClass ?? "—";
}

function hydricSoilValue(env: EnvironmentalResponse | null): string {
  return env?.soil?.hydric ?? "—";
}

/**
 * Format the AirNow row. Buyer asked to ALWAYS show it (even when
 * AQI is in the "Good" range) so the row's only fallback is when
 * AirNow either isn't configured (no API key) or returned no data
 * (rural area, no station within search radius).
 *
 * When the env says `disabled.airnow: true` we hide the row entirely
 * — there's no signal to surface, and a "Not configured" string would
 * just look like a bug to a customer who doesn't know AirNow is a
 * separate feature flag.
 */
function airQualityValue(env: EnvironmentalResponse | null): string {
  const airnow = env?.airnow;
  if (!airnow) return "—";
  return `${airnow.aqi} (${airnow.category})`;
}

function canopyValue(env: EnvironmentalResponse | null): string {
  const c = env?.canopy;
  if (!c || c.canopyPercent == null) return "—";
  const band = canopyDisplayName(c.band);
  return `${Math.round(c.canopyPercent)}% (${band})`;
}

function canopyDisplayName(band: UsfsCanopyBand): string {
  switch (band) {
    case "open": return "Open";
    case "light": return "Light";
    case "moderate": return "Moderate";
    case "heavy": return "Heavy";
    case "dense": return "Dense";
    case "unknown": return "—";
  }
}

function seismicValue(env: EnvironmentalResponse | null): string {
  const s = env?.seismic;
  if (!s || s.pga2pct50yr == null) return "—";
  return `${seismicDisplayName(s.band)} (PGA ${s.pga2pct50yr.toFixed(2)} g)`;
}

function seismicDisplayName(band: SeismicHazardBand): string {
  switch (band) {
    case "very-low": return "Very low";
    case "low": return "Low";
    case "moderate": return "Moderate";
    case "high": return "High";
    case "very-high": return "Very high";
    case "unknown": return "—";
  }
}

function radonValue(env: EnvironmentalResponse | null): string {
  const r = env?.radon;
  if (!r || r.zone == null) return "—";
  switch (r.zone) {
    case 1: return "Zone 1 (high)";
    case 2: return "Zone 2 (moderate)";
    case 3: return "Zone 3 (low)";
  }
}

function climateValue(env: EnvironmentalResponse | null): string {
  const c = env?.climate;
  if (!c || !c.station) return "—";
  const parts: string[] = [];
  if (c.annualPrecipIn != null) {
    parts.push(`${c.annualPrecipIn}" rain`);
  }
  if (c.annualSnowfallIn != null && c.annualSnowfallIn > 0.5) {
    parts.push(`${Math.round(c.annualSnowfallIn)}" snow`);
  }
  if (c.annualMeanTempF != null) {
    parts.push(`${c.annualMeanTempF}°F avg`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}

interface CardRow {
  label: string;
  sideText: string;
  multiline?: boolean;
}

interface CardGroup {
  title: string;
  rows: CardRow[];
}

function buildGroups(
  external: ExternalContextReportData | null,
  env: EnvironmentalResponse | null
): CardGroup[] {
  const hazards: CardRow[] = [
    { label: "Wildfire hazard", sideText: wildfireValue(env) },
    { label: "Seismic hazard", sideText: seismicValue(env) },
    { label: "Radon zone", sideText: radonValue(env) },
    {
      label: "EPA facilities nearby",
      sideText: epaValue(env),
      multiline: true
    }
  ];
  // AirNow row appears only when the source isn't disabled — see
  // `airQualityValue` rationale.
  if (env && !env.disabled.airnow) {
    hazards.push({ label: "Air quality (current)", sideText: airQualityValue(env) });
  }

  return [
    {
      title: "Water",
      rows: [
        { label: "FEMA Zone", sideText: femaZoneValue(external), multiline: true },
        {
          label: "Flood polygons on lot",
          sideText: coverageValue(external?.floodCoverageFraction ?? null)
        },
        {
          label: "Wetlands on lot",
          sideText: coverageValue(external?.wetlandsCoverageFraction ?? null)
        }
      ]
    },
    { title: "Hazards", rows: hazards },
    {
      title: "Ground & cover",
      rows: [
        { label: "Soil drainage", sideText: soilDrainageValue(env) },
        { label: "Hydric soil", sideText: hydricSoilValue(env) },
        { label: "Tree canopy at center", sideText: canopyValue(env) },
        { label: "Climate normals", sideText: climateValue(env), multiline: true }
      ]
    }
  ];
}

export interface EnvironmentalRiskCardProps {
  external: ExternalContextReportData | null;
  /** Phase 3 environmental envelope. Null until the worker call lands;
   *  rows render `—` for env-sourced fields in the meantime so the
   *  layout doesn't shift when the data arrives. */
  environmental: EnvironmentalResponse | null;
  presentation?: InspectorPresentation;
}

export function EnvironmentalRiskCard({
  external,
  environmental,
  presentation = "inspector"
}: EnvironmentalRiskCardProps): ReactElement {
  const drawerRail = presentation === "drawer";
  const groups = buildGroups(external, environmental);

  return (
    <div>
      {groups.map((g, gi) => (
        <div key={g.title}>
          <SubsectionHeader label={g.title} first={gi === 0} />
          {g.rows.map((r) => (
            <SidetextRow
              key={r.label}
              label={r.label}
              sideText={r.sideText}
              multiline={r.multiline === true}
              drawerRail={drawerRail}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
